import { Alert, Button, TableBody, TableCell, TableRow } from '@digdir/designsystemet-react';
import * as Sentry from '@sentry/react';
import { FC, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from '../../../../../styled';
import {
  LeftBorderCell,
  StyledTable
} from '../../../../expertRegistration/json/enhet/upload/fileReview/components/styledComponents';
import { EnergyPlan } from '../../../../../types/building';
import { useUser } from '../../../../../hooks/useUser';
import { getSolarBalanceCsv } from '../../../../../services/api';
import { downloadFile } from '../../../../../utils/utils';
import { userHasBygningsekspertRole } from '../../../utils/stepperUtils';

import { resolveSolarInstrumentationRowLabel } from './solarInstrumentationRowLabel';

const InstrumentationTable = styled(StyledTable)`
  table-layout: fixed;
  width: 100%;
`;

const InstrumentationKeyCell = styled(TableCell)`
  width: 46%;
  min-width: 12rem;
  vertical-align: top;
  overflow-wrap: break-word;
`;

const InstrumentationValueCell = styled(LeftBorderCell)`
  width: 54%;
  vertical-align: top;
  overflow-wrap: anywhere;
`;

const InstrumentationJsonBlock = styled.pre`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: var(--ds-font-size-1, 0.875rem);
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  max-width: 100%;
`;

function renderInstrumentationValue(raw: string): ReactNode {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return raw;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object') {
      return <InstrumentationJsonBlock>{JSON.stringify(parsed, null, 2)}</InstrumentationJsonBlock>;
    }
  } catch {
    /* not JSON */
  }
  return raw;
}

interface EgenprodusertElektrisitetProps {
  energiplan: EnergyPlan;
}

export const EgenprodusertElektrisitetView: FC<EgenprodusertElektrisitetProps> = ({ energiplan }) => {
  const { t } = useTranslation('', { keyPrefix: 'expertToolsJSON.summary.solceller' });
  const { user } = useUser();
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const utdata = energiplan.energimerke?.utdata;
  const energiforbruk = utdata?.energiforbruk;
  const solarInstrumentation = utdata?.solarInstrumentation;

  const egenbruk = energiforbruk?.egenprodusertElektrisitetTilEgenbruk ?? 0;
  const eksport = energiforbruk?.egenprodusertElektrisitetTilEksport ?? 0;
  const hasSolarEnergy = egenbruk > 0 || eksport > 0;
  const hasInstrumentation =
    solarInstrumentation && Object.keys(solarInstrumentation).length > 0;
  const isBygningsekspert = userHasBygningsekspertRole(user);
  const showSimuleringsdetaljer = Boolean(hasInstrumentation && isBygningsekspert);
  const shouldShowDownloadButton =
    isBygningsekspert && window.config.VITE_SOLAR_CSV_DOWNLOAD_ENABLED;

  const shouldShow = hasSolarEnergy || showSimuleringsdetaljer;

  const instrumentationEntries = useMemo(() => {
    if (!solarInstrumentation) {
      return [];
    }
    const entries = Object.entries(solarInstrumentation);
    return entries.filter(
      ([key]) =>
        key !== 'panel.gamma_pdc_converted' && !(key === 'panel.model')
    );
  }, [solarInstrumentation]);

  const handleDownloadCsv = async () => {
    setDownloadError(null);

    try {
      const { data, filename } = await getSolarBalanceCsv(energiplan.registreringId);
      const blobUrl = URL.createObjectURL(data);

      try {
        downloadFile({ uri: blobUrl, filename });
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t('downloadCsvError');
      setDownloadError(errorMessage);
      Sentry.captureException(error);
    }
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      {hasSolarEnergy && (
        <StyledTable>
          <TableBody>
            <TableRow>
              <TableCell>{t('egenbruk')}</TableCell>
              <LeftBorderCell>{egenbruk.toFixed(1)} kWh/år</LeftBorderCell>
            </TableRow>
            <TableRow>
              <TableCell>{t('eksport')}</TableCell>
              <LeftBorderCell>{eksport.toFixed(1)} kWh/år</LeftBorderCell>
            </TableRow>
          </TableBody>
        </StyledTable>
      )}
      {showSimuleringsdetaljer && (
        <>
          <h4 style={{ marginTop: hasSolarEnergy ? 'var(--ds-size-4)' : 0, marginBottom: 'var(--ds-size-2)' }}>
            {t('instrumentation')}
          </h4>
          <InstrumentationTable>
            <TableBody>
              {instrumentationEntries.map(([key, value]) => (
                  <TableRow key={key}>
                    <InstrumentationKeyCell>{resolveSolarInstrumentationRowLabel(key, t)}</InstrumentationKeyCell>
                    <InstrumentationValueCell>{renderInstrumentationValue(value)}</InstrumentationValueCell>
                  </TableRow>
                ))}
              {shouldShowDownloadButton && (
                <TableRow>
                  <InstrumentationKeyCell>{resolveSolarInstrumentationRowLabel('solarBalanceCsv', t)}</InstrumentationKeyCell>
                  <InstrumentationValueCell>
                    <div>
                      <Button onClick={handleDownloadCsv} variant="secondary">
                        {t('downloadCsv')}
                      </Button>
                      {downloadError && (
                        <Alert style={{ marginTop: 'var(--ds-size-2)' }} data-color="danger">
                          {downloadError}
                        </Alert>
                      )}
                    </div>
                  </InstrumentationValueCell>
                </TableRow>
              )}
            </TableBody>
          </InstrumentationTable>
        </>
      )}
    </>
  );
};
